import { useEffect, useState } from 'react'
import { FooterButtons, ProgressStepper, SFError, SingleSelectWithIcon, Text, Title } from 'ui'
import { z } from 'zod'
import {
  EMPLOYMENT_STATUS_DESKTOP,
  EMPLOYMENT_STATUS_MOBILE,
  NO_EMPLOYMENT_STATUSES,
  PAGE_NAME,
} from '~/constants/common'
import { ApplicationType } from '~/constants/dashboard'
import stepperDataSample from '~/constants/stepperData'
import { contactId, opportunityId } from '~/cookie'
import { isFinalStepCompleted } from '~/helperFunctions/isFinalStepCompleted'
import { useLoaderActionData } from '~/hooks/useLoaderActionData'
import { authenticator } from '~/services/auth.server'
import refreshToken from '~/services/refreshToken.server'
import { badRequest } from '~/utils/badRequest'
import { isNewUser } from '~/utils/checkForNewUser'
import { checkPrevModuleData } from '~/utils/checkPrevModuleData'
import { GenericActionData, clientValidation } from '~/utils/clientValidation'
import { isDateGreaterThanToday } from '~/utils/common'
import { getRedirectPath, lightSpeedRelatedFlags } from '~/utils/commonUtils'
import { handleProgressBar } from '~/utils/handleProgressBar'
import { inferSafeParseErrors } from '~/utils/inferSafeParseErrors'
import { PageName } from '~/utils/pageName'
import { scrollToError } from '~/utils/scrollToError'
import { UppercaseFirstLetter } from '~/utils/uppercaseFirstLetter'

import { Alert, Container, Grid, Space } from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import { ActionFunction, LoaderFunction, json, redirect } from '@remix-run/node'
import { Form, useActionData, useLocation, useNavigate, useNavigation } from '@remix-run/react'

import { sessionStorage } from '../../services/session.server'
import saveEmploymentInfo from '../api/employment-information'
import getInfo from '../api/get-information'
import getAllInfo from '../api/overall-get-information'
import saveInfo from '../api/save-information'

export const schema = z.object({
  employmentStatus: z.string({ required_error: 'Please select an option.' }),
  preferredName: z.string().optional(),
  isUSGradAPP: z.string().optional(),
  isLightSpeedUser: z.string().optional(),
  isNewUserInLightSpeedFlow: z.string().optional(),
  requiredUSEmploymentData: z.string().optional(),
  borrowerStepProgress: z.string().optional(),
})
type SchemaFields = z.infer<typeof schema>
type SchemaFieldsErrors = inferSafeParseErrors<typeof schema>
type ActionData = GenericActionData<SchemaFields, SchemaFieldsErrors>

export const action: ActionFunction = async ({ request }) => {
  try {
    const cookie = (await opportunityId.parse(request.headers.get('Cookie'))) || {}
    if (!cookie?.oppID) return redirect('/dashboard')
    const formData = await request.formData()
    const fields = Object?.fromEntries(formData.entries()) as unknown as SchemaFields
    const result = schema?.safeParse(fields)

    if (!result.success) {
      return badRequest({
        fields,
        errors: result.error.flatten(),
        status: false,
        dataType: 'actionData',
      })
    }

    const data = PageName(request)
    const session = await sessionStorage.getSession(request.headers.get('cookie'))
    const accessToken = await refreshToken(request, session)

    const contactCookie = (await contactId.parse(request.headers.get('Cookie'))) || {}

    const body = {
      data: { employmentStatus: fields.employmentStatus },
      opportunity_id: cookie.oppID,
      page_name: PAGE_NAME.employment,
      step_number: 1,
    }

    const bodyNoHistory = {
      ...body,
      data: { ...body.data, yearsOfWorkExp: 0 },
    }
    const isNoEmployment = NO_EMPLOYMENT_STATUSES.includes(fields?.employmentStatus ?? '')
    const isUSGradAPP = fields?.isUSGradAPP === 'true'

    if (isUSGradAPP) {
      if (isNoEmployment) {
        const employmentInfoBody = {
          page_name: data.pageName,
          opportunity_id: cookie.oppID,
          contact_id: contactCookie.contactID,
        }

        try {
          const lsUSbody = {
            ...bodyNoHistory,
            data: { ...bodyNoHistory.data },
          }
          const saveRes = await saveInfo(accessToken, lsUSbody, request)
          const sfRes = await saveEmploymentInfo(accessToken, employmentInfoBody, request)
          if (sfRes?.status === 'Error') {
            const error = sfRes.sf_error
              ? { error: 'Oops !! Something went wrong. Please check the data entered.' }
              : sfRes?.middleware?.validations
            return badRequest({ sfError: error })
          }

          const borrowerProgress = Number(fields?.borrowerStepProgress)
          return {
            redirectToOffer: false,
            redirectTo: borrowerProgress === 4 ? '/finance/reviewAndSubmit' : '/finance',
          }
        } catch {
          return null
        }
      }

      const saveRes = await saveInfo(accessToken, bodyNoHistory, request)
      if (saveRes?.success) {
        return { redirectToOffer: false, redirectTo: '/employment/usgradJob' }
      }
    }

    const bodyToUse = isNoEmployment ? bodyNoHistory : body
    const saveRes = await saveInfo(accessToken, bodyToUse, request)

    if (saveRes?.success) {
      return {
        redirectToOffer: isNoEmployment,
        redirectTo: isNoEmployment ? '/employment/offer' : '/employment/experience',
      }
    }

    return {}
  } catch {
    return {}
  }
}

export const loader: LoaderFunction = async ({ request }) => {
  await authenticator.isAuthenticated(request, {
    failureRedirect: '/login',
  })
  const session = await sessionStorage.getSession(request.headers.get('cookie'))
  const cookie = (await opportunityId.parse(request.headers.get('Cookie'))) || {}
  if (!cookie?.oppID) return redirect('/dashboard')
  const data: any = PageName(request)
  const accessToken = await refreshToken(request, session)
  try {
    const fetchData = await getAllInfo(accessToken, cookie.oppID, request)
    if (isFinalStepCompleted(fetchData?.data?.response)) {
      return redirect('/in-review')
    }
    const redirectTo = getRedirectPath({}, fetchData?.data?.response?.user_details, 'employment')
    if (redirectTo) {
      return redirect(redirectTo)
    }
    const getMissingData: any = checkPrevModuleData(
      'employment_information',
      fetchData?.data?.response,
    )
    if (getMissingData !== '/employment') {
      return redirect(getMissingData)
    }
    const created_at = fetchData?.data?.response?.user_details?.created_at
    const eduInfo = fetchData?.data?.response?.educational_information
    const userDetails = fetchData?.data?.response?.user_details
    const hasWaivedOrHavent = (): boolean => {
      if (!eduInfo) return false

      const { standardizedTest = '', languageTest = '' } = eduInfo

      const hasWaived = standardizedTest.includes('waived') || languageTest.includes('waived')

      const hasHaventTaken =
        standardizedTest.includes('haventTaken') || languageTest.includes('haventTaken')

      return hasWaived || hasHaventTaken
    }

    const hasWaive = hasWaivedOrHavent()
    return json(
      {
        fields: {
          pageData: data,
          oppId: cookie,
          employmentStatus: fetchData?.data?.response?.employment_information?.employmentStatus,
          preferredName: UppercaseFirstLetter(
            fetchData?.data?.response?.user_details?.data?.preferedname,
          ),
          hasWaived: hasWaive,
          isNewUser: isNewUser(created_at),
          isUSGradAPP:
            fetchData?.data?.response?.user_details?.opportunity_type === ApplicationType.US_GRAD,
          programStartDate: eduInfo?.programStartDate
            ? eduInfo?.programStartDate
            : userDetails.data?.programStartDate,
          borrowerStepProgress: fetchData?.data?.response?.user_details?.borrower_step_progress,
          ...lightSpeedRelatedFlags(fetchData?.data?.response.user_details),
        },
        dataType: 'loaderData',
      },
      200,
    )
  } catch (err) {
    return null
  }
}

export default function Index() {
  const navigate = useNavigate()
  const location = useLocation()
  const loaderActionData = useLoaderActionData<ActionData>()
  const [data, setData] = useState<ActionData>(loaderActionData)
  const navigation = useNavigation()
  const isMobile = useMediaQuery('(max-width: 767px)')
  const isSubmitting =
    navigation.state === 'submitting' || (navigation.state === 'loading' && !!navigation.formAction)
  const getBackButtonPath = (): string => {
    const fields = loaderActionData?.fields

    if (isDateGreaterThanToday(fields?.programStartDate)) return '/education'
    if (fields?.requiredUSEmploymentData) return '/education/type'
    return fields?.hasWaived ? '/education/test' : '/education/score'
  }

  useEffect(() => {
    if (loaderActionData?.redirectTo) {
      try {
        const data = { skipEmploymentTillOffer: loaderActionData?.redirectToOffer ? true : false }
        handleProgressBar(data)
        navigate(loaderActionData?.redirectTo)
      } catch (error) {}
    }
  }, [loaderActionData?.redirectTo])

  useEffect(() => {
    setData(loaderActionData)
  }, [loaderActionData.dataType])
  const actionData = useActionData<ActionData>()
  useEffect(() => {
    if (actionData) scrollToError(actionData as ActionData)
  }, [actionData])
  return (
    <>
      <Container size="lg" pt={30}>
        <ProgressStepper
          currentLocation={location?.pathname}
          steps={stepperDataSample}
          isNewUser={loaderActionData?.fields?.isNewUser}
        />
      </Container>
      <Container className="mainContainer" size="lg">
        <Form method="post" onChange={clientValidation(schema, data, setData)}>
          <Grid>
            <Grid.Col span={{ sm: 12, md: 3 }}>
              <Title order={isMobile ? 4 : 3} variant="blue" fontWeight="400">
                Okay,{' '}
                <span style={{ textTransform: 'capitalize', wordWrap: 'break-word' }}>
                  {data?.fields?.preferredName}
                </span>{' '}
                now let&apos;s review{' '}
              </Title>
              <Title order={isMobile ? 4 : 3} variant="yellow" fontWeight="700">
                your employment history.
              </Title>
            </Grid.Col>
            <Grid.Col span={{ sm: 12, md: 9 }} className="desktopRightContainer">
              <Grid>
                <Grid.Col span={{ sm: 12 }}>
                  <SingleSelectWithIcon
                    id="employmentStatus"
                    style={{ paddingTop: '1.75rem' }}
                    error={data?.errors}
                    defaultValue={data?.fields?.employmentStatus}
                    options={isMobile ? EMPLOYMENT_STATUS_MOBILE : EMPLOYMENT_STATUS_DESKTOP}
                  />
                </Grid.Col>
                <input
                  type="hidden"
                  name="preferredName"
                  id="preferredName"
                  value={data?.fields?.preferredName}
                />
                <input
                  type="hidden"
                  name="borrowerStepProgress"
                  id="borrowerStepProgress"
                  value={data?.fields?.borrowerStepProgress}
                />

                <input
                  type="hidden"
                  name="isUSGradAPP"
                  id="isUSGradAPP"
                  value={data?.fields?.isUSGradAPP}
                />
              </Grid>
            </Grid.Col>
          </Grid>
          <Space h={40} />
          <SFError error={loaderActionData?.sfError} />
          <FooterButtons
            backButtonId="back"
            id="next"
            oppId={data?.fields?.oppId?.oppID}
            pageData={data?.fields?.pageData}
            outlineButtonOnClick={() => navigate(getBackButtonPath())}
            showOutlineButton={true}
            showFilledButton={true}
            uppercase={true}
            filledButtonText="next"
            loading={isSubmitting}
            footerBack
          />
        </Form>
      </Container>
    </>
  )
}
